import { filterAnyCollateralPayableTokens } from '@ui/config/brand';
import { useBrand } from '@ui/config/BrandProvider';
import { getPayablePoolTokens } from '@ui/constants/tokens/poolTokens';
import { usePayTokenPriceLatest } from '@ui/features/Trade/hooks/usePayTokenPriceLatest';
import { useDataChainId } from '@ui/hooks/datachain';
import { useTradePairInfos } from '@ui/hooks/trade/useTradePairInfos';
import { Position } from '@ui/services/contract/hooks/TradingReaderFacet/useGetPositionV2/types';
import { useTranslation } from 'next-i18next';
import { useEffect, useMemo } from 'react';

import { useGetPayableTokenBalance } from '../../hooks/useGetPayableTokenBalance';
import { CollateralInput } from '../CollateralInput';
import { useMaxInput } from '../TradeModule/hooks/useMaxInput';
import { useCollateralToken } from '../TradeModule/store/useCollateralToken';
import { getCollateralAmountErrorMessage } from '../TradeModule/utils/validateCollateralAmount';
import { getDepositCollateralAmountErrorMessage } from '../TradeModule/utils/validateDepositCollateralAmount';
import { useDepositCollateralGasFee } from './hooks/useDepositCollateralGasFee';
import { useDepositCollateralInput } from './hooks/useDepositCollateralInput';
import { useLeverageChangeLatestPrice } from './hooks/useLeverageChange';
import { useMinLeverage } from './hooks/useMinLeverage';

export const DepositCollateralInput = ({
  position,
}: {
  position: Position;
}) => {
  const chainId = useDataChainId();
  const brand = useBrand();
  const { t } = useTranslation();

  const setCollateralToken = useCollateralToken((s) => s.setCollateralToken);
  const payToken =
    useCollateralToken((s) => s.useCollateralTokenValue()) ??
    position.collateralToken;

  // Set collateral token to position's collateral token on mount
  useEffect(() => {
    setCollateralToken(position.collateralToken, chainId);
  }, [position.collateralToken, chainId, setCollateralToken]);

  // const payTokenBalance = usePayTokenBalance(position.collateralToken);
  const payTokenBalance = useGetPayableTokenBalance();
  const payTokenPrice = usePayTokenPriceLatest(payToken);
  // const payTokenPrice =
  //   usePriceLatest(position.collateralToken.symbol) ?? ZERO_BIG_NUMBER;

  const { input, setInput, useValidation } = useDepositCollateralInput();
  const validation = useValidation({
    position: {
      ...position,
      collateralToken: payToken,
    },
  });
  const change = useLeverageChangeLatestPrice({
    position,
  });

  const pairInfos = useTradePairInfos();

  const pairInfo = useMemo(() => {
    return pairInfos?.find(
      (v) => v.address.toLowerCase() === position.pair.address.toLowerCase(),
    );
  }, [pairInfos, position.pair.address]);

  const maxInput = useMaxInput({
    pairInfos: pairInfo,
    leverage: change?.prev.toNumber(),
    payTokenPrice,
    spreadPercentage: 0, // no spread for deposit collateral
  });

  const minLeverage = useMinLeverage({ position });
  const { data: gas } = useDepositCollateralGasFee(position);

  const availableMarginTokens = useMemo(() => {
    const poolTokens = getPayablePoolTokens(
      chainId,
      position.collateralToken.lvTokenAddress,
    );

    return filterAnyCollateralPayableTokens(poolTokens, brand);
  }, [brand, chainId, position.collateralToken.lvTokenAddress]);

  return (
    <CollateralInput
      balanceLabelPrefix={`${t('common.Max')}: `}
      gasFee={gas}
      getErrorMessage={(isInputToken: boolean) =>
        getDepositCollateralAmountErrorMessage({
          t,
          error: validation.error,
          isInputToken,
          symbol: payToken?.displaySymbol,
          maxInput,
          leverage: change?.next?.toNumber(),
          minLeverage,
          chainId,
        })
      }
      onInput={(value) => {
        setInput({
          value,
        });
      }}
      tokenBalance={payTokenBalance}
      tokenPrice={payTokenPrice}
      token={payToken}
      setToken={(token) => {
        setCollateralToken(token, chainId);
      }}
      value={input.value}
      overridePayableTokens={availableMarginTokens}
    />
  );
};
